import { STATUS_COLORS, badgeStyle } from '../../utils/statusColors';
import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import DoctorBottomNav from '../../components/doctor/DoctorBottomNav';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import { getDoctorAppointments, updateAppointmentStatus } from '../../services/doctorService';
import { formatDate, formatTime12, formatCurrency, todayISO } from '../../utils/helpers';

function yesterdayISO() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}
function nWeeksAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n * 7);
  return d.toISOString().split('T')[0];
}
function nMonthsAgo(n) {
  const d = new Date(); d.setMonth(d.getMonth() - n);
  return d.toISOString().split('T')[0];
}

// ── Status config ──────────────────────────────────────────────────────────
// STATUS derived from centralized STATUS_COLORS
const STATUS = Object.fromEntries(
  ['pending','confirmed','checked-in','completed','cancelled'].map(k => [
    k, {
      label:  STATUS_COLORS[k].label,
      dot:    STATUS_COLORS[k].hex,
      bg:     STATUS_COLORS[k].bg,
      text:   STATUS_COLORS[k].text,
      border: STATUS_COLORS[k].border,
      icon:   STATUS_COLORS[k].faIcon,
    }
  ])
);

const FLOW = ['pending','confirmed','checked-in','completed'];

// ── Patient Detail Modal ───────────────────────────────────────────────────
function PatientModal({ appt, onClose, onSaved }) {
  const navigate   = useNavigate();
  const [status,       setStatus]       = useState(appt.status);
  const [notes,        setNotes]        = useState(appt.notes || '');
  const [saving,       setSaving]       = useState(false);
  const [checkinToken, setCheckinToken] = useState('');
  const [needsCheckin, setNeedsCheckin] = useState(false);
  const patient   = appt.patientId;
  const payment   = appt.paymentId;
  const cfg       = STATUS[appt.status] || STATUS.pending;
  const isOffline = appt.isOffline;
  const isClinic  = appt.type === 'clinic' && !isOffline;
  const patientName = isOffline
    ? (appt.patients?.[0]?.name || 'Walk-in Patient')
    : (patient?.name || 'Patient');

  // clinic appointments need checkin before status update
  // show checkin input when: clinic, confirmed, not already checked-in
  const requiresCheckinInput = isClinic && appt.status === 'confirmed' && status !== 'cancelled';

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status, notes };
      if (requiresCheckinInput && checkinToken.trim()) {
        payload.checkinToken = checkinToken.trim();
      }
      await updateAppointmentStatus(appt._id, payload);
      toast.success('Updated successfully');
      setNeedsCheckin(false);
      onSaved();
      onClose();
    } catch (err) {
      // Backend says checkin required — show the checkin input
      if (err.response?.data?.requiresCheckin) {
        setNeedsCheckin(true);
        toast.error(err.response.data.error || 'Please provide QR or Booking ID first');
      } else {
        toast.error(err.message || 'Update failed');
      }
    }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="bottom-sheet p-5 max-h-[92vh] overflow-y-auto">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />

        {/* Status banner */}
        <div className="flex items-center gap-3 p-3 rounded-2xl mb-4"
          style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: cfg.dot + '30' }}>
            <i className={`fa-solid ${cfg.icon} text-base`} style={{ color: cfg.dot }} />
          </div>
          <div>
            <p className="text-xs font-semibold" style={{ color: cfg.text }}>Current Status</p>
            <p className="text-base font-bold" style={{ color: cfg.text }}>{cfg.label}</p>
          </div>
          {isOffline && (
            <span className="ml-auto text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
              Walk-in
            </span>
          )}
        </div>

        {/* Patient info */}
        <div className="flex items-center gap-3 mb-4">
          {!isOffline && <Avatar src={patient?.profileImg} name={patientName} size="md" />}
          {isOffline && (
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-person-walking text-amber-600 text-base" />
            </div>
          )}
          <div>
            <p className="font-bold text-text-primary">{patientName}</p>
            <p className="text-xs text-text-muted">
              {!isOffline && patient?.age && `Age ${patient.age} · ${patient.gender}`}
              {isOffline && appt.patients?.[0] && `Age ${appt.patients[0].age} · ${appt.patients[0].gender}`}
            </p>
          </div>
        </div>

        {/* Booking info grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { label:'Token', value:`#${appt.tokenNumber}`, icon:'fa-hashtag', color:'text-primary' },
            { label:'Time',  value:formatTime12(appt.slotTime), icon:'fa-clock', color:'text-text-primary' },
            { label:'Date',  value:formatDate(appt.appointmentDate), icon:'fa-calendar', color:'text-text-primary' },
            { label:'Type',  value:appt.type === 'video' ? 'Video Call' : (isOffline ? 'Walk-in' : 'Clinic Visit'),
              icon: appt.type === 'video' ? 'fa-video' : (isOffline ? 'fa-person-walking' : 'fa-hospital'),
              color: appt.type === 'video' ? 'text-blue-600' : isOffline ? 'text-amber-600' : 'text-primary' },
          ].map(item => (
            <div key={item.label} className="bg-surface rounded-xl p-3 border border-border">
              <p className="text-[10px] text-text-muted font-medium mb-1">{item.label}</p>
              <div className="flex items-center gap-1.5">
                <i className={`fa-solid ${item.icon} ${item.color} text-xs`} />
                <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Payment */}
        {payment && (
          <div className="flex items-center justify-between p-3 rounded-xl border border-border mb-4"
            style={{ background: payment.status === 'released' ? '#F0FDF4' : '#FFFBEB' }}>
            <div className="flex items-center gap-2">
              <i className={`fa-solid fa-indian-rupee-sign text-sm ${payment.status === 'released' ? 'text-green-600' : 'text-amber-600'}`} />
              <span className="text-sm font-bold text-text-primary">{formatCurrency(payment.totalAmount)}</span>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              payment.status === 'released' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {payment.status === 'released' ? 'Payment Released' : 'Pending Release'}
            </span>
          </div>
        )}

        {/* Patients list */}
        {appt.patients?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Patients</p>
            <div className="space-y-1.5">
              {appt.patients.map((p, i) => (
                <div key={i} className="flex items-center gap-2 bg-surface p-2.5 rounded-xl border border-border">
                  <div className="w-6 h-6 rounded-full bg-primary-light flex items-center justify-center text-[10px] font-bold text-primary flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-text-primary">{p.name}</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {p.age && <span className="text-xs text-text-muted">{p.age}y</span>}
                      {p.gender && <span className="text-xs text-text-muted">· {p.gender}</span>}
                      {p.address && <span className="text-xs text-text-muted">· {p.address}</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    p.isOldPatient ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {p.isOldPatient ? 'Returning' : 'New'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status change — only if not final state */}
        {!['cancelled','completed'].includes(appt.status) && (
          <div className="mb-4">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Update Status</p>
            <div className="grid grid-cols-2 gap-2">
              {FLOW.map(s => {
                const c   = STATUS[s];
                const sel = status === s;
                return (
                  <button key={s} onClick={() => setStatus(s)}
                    className="flex items-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95"
                    style={{
                      background:  sel ? c.dot : c.bg,
                      borderColor: sel ? c.dot : c.border,
                    }}>
                    <i className={`fa-solid ${c.icon} text-sm`}
                      style={{ color: sel ? '#fff' : c.dot }} />
                    <span className="text-xs font-bold"
                      style={{ color: sel ? '#fff' : c.text }}>
                      {c.label}
                    </span>
                    {sel && <i className="fa-solid fa-check text-white text-xs ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Chat button — for video appointments instead of QR scan */}
        {appt.type === 'video' && appt.patientId && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-comments text-blue-600 text-base" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-blue-800">Video Consultation</p>
              <p className="text-[11px] text-blue-600">Chat with this patient before or during the call</p>
            </div>
            <button
              onClick={() => {
                const patientId = typeof appt.patientId === 'object' ? appt.patientId._id : appt.patientId;
                const doctorId  = appt.doctorId || appt.doctor?._id;
                // Conversation ID = sorted [patientId, doctorId] joined with _
                const convId    = [patientId, doctorId].sort().join('_');
                onClose();
                navigate(`/doctor/chat/${convId}`);
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl active:scale-95 flex-shrink-0">
              <i className="fa-solid fa-comment-dots" />
              Chat
            </button>
          </div>
        )}

        {/* Notes */}
        <div className="mb-4">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Notes / Prescription</p>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            rows={2} placeholder="Add notes, prescription, or observations..."
            className="input resize-none text-sm" />
        </div>

        {/* Booking ID intentionally hidden from doctor — patient shows it at clinic */}

        {/* Checkin token input — shown for clinic confirmed appointments */}
        {(requiresCheckinInput || needsCheckin) && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-2xl">
            <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1.5">
              <i className="fa-solid fa-qrcode text-amber-600" />
              Patient Check-in Required
            </p>
            <p className="text-[11px] text-amber-600 mb-2">
              Enter the patient's QR token or Booking ID to verify before updating status.
            </p>
            <input
              type="text"
              value={checkinToken}
              onChange={e => setCheckinToken(e.target.value)}
              placeholder="QR token or Booking ID (e.g. FARISTAA-BKG-...)"
              className="input text-xs font-mono"
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 border-2 border-border rounded-2xl text-sm font-semibold text-text-secondary">
            Close
          </button>
          {!['cancelled','completed'].includes(appt.status) && (
            <button onClick={save} disabled={saving}
              className="flex-1 py-3 rounded-2xl text-sm font-bold text-white transition-all active:scale-95"
              style={{ background: STATUS[status]?.dot || '#0D9488' }}>
              {saving
                ? <i className="fa-solid fa-spinner animate-spin" />
                : <span><i className="fa-solid fa-floppy-disk mr-1.5" />Save</span>}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Appointment Card ───────────────────────────────────────────────────────
function AppointmentCard({ appt, onSelect }) {
  const cfg       = STATUS[appt.status] || STATUS.pending;
  const isOffline = appt.isOffline;
  const patientName = isOffline
    ? (appt.patients?.[0]?.name || 'Walk-in')
    : (appt.patientId?.name || 'Patient');
  const patient = appt.patientId;

  return (
    <button onClick={() => onSelect(appt)}
      className="w-full text-left rounded-2xl border-l-4 bg-white border border-border overflow-hidden active:scale-98 transition-all shadow-sm"
      style={{ borderLeftColor: cfg.dot }}>
      <div className="flex items-center gap-3 p-3">
        {/* Token badge */}
        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm"
          style={{ background: cfg.bg, color: cfg.dot }}>
          #{appt.tokenNumber}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-bold text-text-primary truncate">{patientName}</p>
            {isOffline && (
              <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                Walk-in
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-muted flex items-center gap-1">
              <i className="fa-solid fa-clock text-[10px]" />{formatTime12(appt.slotTime)}
            </span>
            <span className={`text-xs font-medium flex items-center gap-1 ${
              appt.type === 'video' ? 'text-blue-600' : 'text-primary'
            }`}>
              <i className={`fa-solid ${appt.type === 'video' ? 'fa-video' : 'fa-hospital'} text-[10px]`} />
              {appt.type === 'video' ? 'Video' : 'Clinic'}
            </span>
            {!isOffline && patient?.age && (
              <span className="text-xs text-text-muted">{patient.age}y · {patient.gender}</span>
            )}
            {isOffline && appt.patients?.[0]?.age && (
              <span className="text-xs text-text-muted">{appt.patients[0].age}y</span>
            )}
          </div>
        </div>

        {/* Status pill */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
          <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border"
            style={badgeStyle(appt.status)}>
            <i className={`fa-solid ${cfg.icon} text-[9px]`} />
            {cfg.label}
          </span>
          <i className="fa-solid fa-chevron-right text-slate-300 text-[10px]" />
        </div>
      </div>
    </button>
  );
}

// ── Status filter tab ──────────────────────────────────────────────────────
function StatusTab({ value, active, count, onClick }) {
  const sc  = STATUS_COLORS[value] || STATUS_COLORS.all;
  const cfg = { label: sc.label, dot: sc.hex, bg: sc.bg, text: sc.text };
  return (
    <button onClick={onClick}
      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all active:scale-95"
      style={{
        background:  active ? sc.hex    : sc.bg,
        borderColor: active ? sc.hex    : sc.border,
        color:       active ? '#ffffff' : sc.text,
      }}>
      {value !== 'all' && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {cfg.label}
      {count > 0 && (
        <span className="text-[9px] font-black opacity-80">({count})</span>
      )}
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function DoctorAppointments() {
  const { role, user } = useAuth();


  // dateRange: { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } | null → null means Today (single date)
  const [date, setDate]           = useState(todayISO());
  const [dateRange, setDateRange] = useState(null); // null = single date mode
  const [typeFilter, setTypeFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [appointments, setAppts]  = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      // Range mode (1 week, 2 week, 1 month buttons) → fromDate + toDate
      if (dateRange) {
        params.fromDate = dateRange.from;
        params.toDate   = dateRange.to;
      } else {
        params.date = date;
      }
      if (typeFilter   !== 'all') params.type   = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getDoctorAppointments(params);
      setAppts(res.appointments || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [date, dateRange, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Count per status
  const counts = appointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  const STATUS_TABS = ['all','pending','confirmed','checked-in','completed','cancelled'];
  const TYPE_TABS   = [['all','All Types'],['clinic','Clinic'],['video','Video']];


  // ── Staff permission guard (must be AFTER all hooks) ──────────────────
  if (role === 'staff' && !user?.permissions?.['appointments']) {
    return (
      <div className="page-container flex flex-col items-center justify-center py-20 gap-4 text-center px-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
          <i className="fa-solid fa-lock text-red-500 text-3xl" />
        </div>
        <h2 className="text-lg font-bold text-text-primary">Access Restricted</h2>
        <p className="text-sm text-text-muted leading-relaxed">
          You don&apos;t have permission to access this section.<br />
          Contact your doctor to enable it.
        </p>
        <button onClick={() => window.history.back()}
          className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-bold">
          Go Back
        </button>
      </div>
    );
  }


  return (
    <div className="page-container">
      <Header title="Appointments" showBack />

      {/* Sticky filter bar */}
      <div className="sticky top-14 z-20 bg-white border-b border-border">
        {/* Date picker + navigation */}
        <div className="px-4 pt-3 pb-2 space-y-2">
          {/* Quick date buttons — Today/Yesterday = single date, rest = range */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {[
              { label: 'Today',    type: 'single', iso: todayISO(),    color: 'bg-primary border-primary text-white',       off: 'bg-primary-light border-primary/30 text-primary' },
              { label: 'Yesterday',type: 'single', iso: yesterdayISO(),color: 'bg-purple-600 border-purple-600 text-white', off: 'bg-purple-50 border-purple-200 text-purple-700' },
              { label: '1 Week',   type: 'range',  from: nWeeksAgo(1), to: todayISO(), color: 'bg-blue-600 border-blue-600 text-white',    off: 'bg-blue-50 border-blue-200 text-blue-700' },
              { label: '2 Weeks',  type: 'range',  from: nWeeksAgo(2), to: todayISO(), color: 'bg-indigo-600 border-indigo-600 text-white', off: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
              { label: '3 Weeks',  type: 'range',  from: nWeeksAgo(3), to: todayISO(), color: 'bg-violet-600 border-violet-600 text-white', off: 'bg-violet-50 border-violet-200 text-violet-700' },
              { label: '1 Month',  type: 'range',  from: nMonthsAgo(1),to: todayISO(), color: 'bg-rose-600 border-rose-600 text-white',     off: 'bg-rose-50 border-rose-200 text-rose-700' },
            ].map(b => {
              const isActive = b.type === 'range'
                ? dateRange?.from === b.from && dateRange?.to === b.to
                : !dateRange && date === b.iso;
              const handleClick = () => {
                if (b.type === 'range') {
                  setDateRange({ from: b.from, to: b.to });
                } else {
                  setDateRange(null);
                  setDate(b.iso);
                }
              };
              return (
                <button key={b.label} onClick={handleClick}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all whitespace-nowrap ${isActive ? b.color : b.off}`}>
                  {b.label}
                </button>
              );
            })}
          </div>
          {/* Manual date input */}
          <div className="flex items-center gap-2 bg-surface border border-border rounded-xl px-3 py-2">
            <i className="fa-solid fa-calendar text-primary text-sm" />
            <input type="date" value={dateRange ? dateRange.to : date} onChange={e => { setDateRange(null); setDate(e.target.value); }}
              className="flex-1 bg-transparent outline-none text-sm text-text-primary font-semibold" />
            <span className="text-xs text-text-muted font-medium bg-white px-2 py-0.5 rounded-lg border border-border">
{dateRange ? `${appointments.length} appts (${dateRange.from} → ${dateRange.to})` : `${appointments.length} appts`}
            </span>
          </div>
        </div>

        {/* Type filter */}
        <div className="flex gap-2 px-4 pb-2 overflow-x-auto scrollbar-hide">
          {TYPE_TABS.map(([k,l]) => (
            <button key={k} onClick={() => setTypeFilter(k)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                typeFilter === k
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-500 border-slate-200'
              }`}>
              {k === 'video' && <i className="fa-solid fa-video text-[10px]" />}
              {k === 'clinic' && <i className="fa-solid fa-hospital text-[10px]" />}
              {k === 'all' && <i className="fa-solid fa-list text-[10px]" />}
              {l}
            </button>
          ))}
        </div>

        {/* Status filter — color coded */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide">
          {STATUS_TABS.map(s => (
            <StatusTab key={s} value={s} active={statusFilter === s}
              count={s === 'all' ? appointments.length : (counts[s] || 0)}
              onClick={() => setStatusFilter(s)} />
          ))}
        </div>
      </div>

      {/* List */}
      <div className="px-4 pt-3 pb-24 space-y-2">
        {loading ? <InlineLoader text="Loading appointments..." /> :
         appointments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <i className="fa-solid fa-calendar-xmark text-3xl text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-500">No appointments found</p>
            <p className="text-xs text-slate-400 mt-1">Try changing the date or filters</p>
          </div>
        ) : appointments.map(a => (
          <AppointmentCard key={a._id} appt={a} onSelect={setSelected} />
        ))}
      </div>

      {selected && (
        <PatientModal appt={selected} onClose={() => setSelected(null)} onSaved={load} />
      )}

      <DoctorBottomNav />
    </div>
  );
}
